import BaseSelect from 'components/Selects/BaseSelect/BaseSelect';
import { findSelectOption } from 'components/Selects/BaseSelect/utils/baseSelect.utils';
import BaseModal from 'components/Modals/BaseModal';
import { useEffect, useMemo, useState } from 'react';
import { Button, Col, Form, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import { FaInfoCircle } from 'react-icons/fa';
import styles from '../css/ServiceManager.module.css';
import type {
  AddWorkplaceRequest,
  UpdateWorkplaceRequest,
  Workplace,
  WorkplaceMode,
} from '../types/Deployments.types';

const MODES: WorkplaceMode[] = ['managed', 'standalone', 'observe', 'disabled'];

const MODE_OPTIONS = MODES.map((m) => ({ value: m, label: m }));

const FIELD_HELP = {
  name: 'Human-readable label shown in the Deployments list (e.g. "Staging", "QA"). Free text.',
  id: 'Integer workplace_id from the licensing database. Identifies this workplace across the system. Cannot be changed once the workplace exists.',
  prefix:
    'Short identifier used in PM2 process names — e.g. prefix "STAGING" produces "STAGING-AlarmService". Alphanumeric + dashes only. Must be unique across workplaces on this supervisor. Auto-derived from Name on first edit.',
  port: "TCP port the backend's FusionBackendServer listens on. Must be unique across all workplaces on this supervisor.",
  rootDir:
    "Absolute filesystem path on the supervisor host containing this workplace's backend code. Must exist on disk — verified server-side. Auto-suggested from an existing workplace's path; edit if your new deployment lives elsewhere.",
  mode: 'How the supervisor manages this workplace. managed = full ownership (spawn, restart, reap). standalone = workplace manages its own processes. observe = monitored only, no control. disabled = workplace is registered but inactive.',
  serviceFlags:
    'Per-workplace overrides for each known service. Default ON for everything; uncheck to keep a service off for this workplace regardless of the global flag.',
  reason:
    'Free-text note logged to command history for audit (e.g. "Standing up QA deployment"). Required so every workplace mutation has a paper trail.',
};

const nextFreePort = (existingPorts: number[]) =>
  existingPorts.length === 0 ? 4050 : Math.max(...existingPorts) + 1;

const deriveDefaultPrefix = (name: string) => name.toUpperCase().replace(/[^A-Z0-9-]/g, '');

type Mode = 'add' | 'edit';

interface WorkplaceFormModalProps {
  show: boolean;
  mode: Mode;
  onClose: () => void;
  /**
   * Submit handler. For add mode, called with a full AddWorkplaceRequest.
   * For edit mode, called with a subset patch (UpdateWorkplaceRequest) + the
   * target wpIndex; the caller is responsible for routing to the right endpoint.
   */
  onSubmit: (
    payload:
      | { mode: 'add'; body: AddWorkplaceRequest }
      | { mode: 'edit'; wpIndex: number; body: UpdateWorkplaceRequest },
  ) => Promise<void>;
  /** Existing workplace prefixes (used for live collision check; pass current's own prefix in `excludePrefix` when editing). */
  existingPrefixes: string[];
  /** Existing workplace ports (used for live collision check). */
  existingPorts: number[];
  /** Existing workplace ids (used for live collision check). Only used in add mode. */
  existingIds: number[];
  /** Env-flag names known to the supervisor (union of all workplace + global services). Only used in add mode. */
  knownEnvFlags: string[];
  /**
   * Optional. Base path used to auto-suggest the Root directory in add mode
   * (e.g. "/home/stagdemoverwatch/BackendServer_Fusion_"). Suggested value is
   * `${suggestedRootBase}${prefix}`. Derived by the caller from an existing
   * workplace's rootDir; falls back to a generic placeholder when empty.
   */
  suggestedRootBase?: string;
  /** When editing, the current workplace whose fields we pre-populate. Required for mode==='edit'. */
  initialWorkplace?: Workplace;
}

interface FormState {
  name: string;
  id: string;
  prefix: string;
  rootDir: string;
  mode: WorkplaceMode;
  port: string;
  serviceFlags: Record<string, boolean>;
  reason: string;
}

const blankForm = (knownEnvFlags: string[], suggestedPort: number): FormState => ({
  name: '',
  id: '',
  prefix: '',
  rootDir: '',
  mode: 'managed',
  // Suggest the next free port so the user can keep typing.
  port: String(suggestedPort),
  // Default every known flag to true — most workplaces want the same service
  // matrix as global; user unchecks the ones they don't want.
  serviceFlags: Object.fromEntries(knownEnvFlags.map((f) => [f, true])),
  reason: '',
});

const formFromWorkplace = (wp: Workplace): FormState => ({
  name: wp.name,
  id: String(wp.id),
  prefix: wp.prefix,
  rootDir: wp.rootDir,
  mode: wp.mode,
  port: String(wp.port),
  // Per spec §2 Phase 4, PATCH body does NOT include serviceFlags. We still
  // hold them in state (display-only) but they aren't sent on edit submit.
  serviceFlags: Object.fromEntries(wp.services.map((s) => [s.envFlag, s.effective])),
  reason: '',
});

/**
 * Workplace form modal — add (FE-SPEC Phase 3) and edit (Phase 4).
 *
 * Add mode: all six fields are required; service-flags matrix is included
 * in the body. Mirrors backend validation client-side (prefix/port/id
 * collision + format) for instant feedback.
 *
 * Edit mode: ID is shown disabled (not editable per spec); the service-flags
 * matrix is hidden because PATCH body cannot change them. Submit sends the
 * five editable fields; backend treats the body as a subset patch.
 *
 * Server-side validation still fires (root-dir existence in particular only
 * the supervisor can check). 4xx responses surface inline as a form-level
 * error notice.
 */
export default function WorkplaceFormModal({
  show,
  mode,
  onClose,
  onSubmit,
  existingPrefixes,
  existingPorts,
  existingIds,
  knownEnvFlags,
  suggestedRootBase,
  initialWorkplace,
}: WorkplaceFormModalProps) {
  const suggestedPort = useMemo(() => nextFreePort(existingPorts), [existingPorts]);

  const rootDirBase = suggestedRootBase || '';
  const deriveRootDir = (prefix: string) =>
    rootDirBase && prefix ? `${rootDirBase}${prefix}` : '';

  const [form, setForm] = useState<FormState>(() =>
    mode === 'edit' && initialWorkplace
      ? formFromWorkplace(initialWorkplace)
      : blankForm(knownEnvFlags, suggestedPort),
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  // Track manual edits on Prefix / Root directory — once the user types in
  // either, we stop auto-deriving it from upstream fields. Always true in edit
  // mode (values came from the existing workplace, not from a keystroke).
  const [prefixManuallyEdited, setPrefixManuallyEdited] = useState(mode === 'edit');
  const [rootDirManuallyEdited, setRootDirManuallyEdited] = useState(mode === 'edit');

  // Reset whenever the modal is reopened.
  useEffect(() => {
    if (show) {
      setForm(
        mode === 'edit' && initialWorkplace
          ? formFromWorkplace(initialWorkplace)
          : blankForm(knownEnvFlags, suggestedPort),
      );
      setSubmitError(null);
      setSubmitting(false);
      setPrefixManuallyEdited(mode === 'edit');
      setRootDirManuallyEdited(mode === 'edit');
    }
  }, [show, knownEnvFlags, mode, initialWorkplace, suggestedPort]);

  const errors = useMemo(() => {
    const out: Partial<Record<keyof FormState, string>> = {};
    // Exclude the workplace's own prefix/port from collision lists when editing
    // (the value can stay the same on a partial patch).
    const otherPrefixes =
      mode === 'edit' && initialWorkplace
        ? existingPrefixes.filter((p) => p !== initialWorkplace.prefix)
        : existingPrefixes;
    const otherPorts =
      mode === 'edit' && initialWorkplace
        ? existingPorts.filter((p) => p !== initialWorkplace.port)
        : existingPorts;

    if (!form.name.trim()) out.name = 'Required';
    if (mode === 'add') {
      if (!form.id.trim()) out.id = 'Required';
      else if (!/^\d+$/.test(form.id.trim())) out.id = 'Must be an integer';
      else if (existingIds.includes(Number(form.id.trim()))) out.id = 'Already in use';
    }
    if (!form.prefix.trim()) out.prefix = 'Required';
    else if (!/^[A-Za-z0-9-]+$/.test(form.prefix.trim()))
      out.prefix = 'Letters, numbers, and dashes only';
    else if (otherPrefixes.includes(form.prefix.trim())) out.prefix = 'Already in use';
    if (!form.rootDir.trim()) out.rootDir = 'Required';
    if (!form.port.trim()) out.port = 'Required';
    else if (!/^\d+$/.test(form.port.trim())) out.port = 'Must be an integer';
    else if (otherPorts.includes(Number(form.port.trim()))) out.port = 'Already in use';
    // Reason is required client-side so every workplace mutation has an audit
    // trail entry; backend treats it as optional but we always send a value.
    if (!form.reason.trim()) out.reason = 'Required for audit trail';
    return out;
  }, [form, mode, initialWorkplace, existingPrefixes, existingPorts, existingIds]);

  const canSubmit = Object.keys(errors).length === 0 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      if (mode === 'edit' && initialWorkplace) {
        const body: UpdateWorkplaceRequest = {
          name: form.name.trim(),
          prefix: form.prefix.trim(),
          rootDir: form.rootDir.trim(),
          mode: form.mode,
          port: Number(form.port.trim()),
          reason: form.reason.trim() || undefined,
          // Send the workplace's current lockVersion so the supervisor can
          // reject the patch with 409 if someone else modified it between
          // the listing fetch and this submit. Parent's `initialWorkplace`
          // refreshes from the latest payload after a 409 refetch, so the
          // next submit picks up the new value automatically.
          lockVersion: initialWorkplace.lockVersion,
        };
        await onSubmit({ mode: 'edit', wpIndex: initialWorkplace.index, body });
      } else {
        const body: AddWorkplaceRequest = {
          name: form.name.trim(),
          id: Number(form.id.trim()),
          prefix: form.prefix.trim(),
          rootDir: form.rootDir.trim(),
          mode: form.mode,
          port: Number(form.port.trim()),
          serviceFlags: form.serviceFlags,
          reason: form.reason.trim() || undefined,
        };
        await onSubmit({ mode: 'add', body });
      }
      onClose();
    } catch (err) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const status = (err as any)?.response?.status;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const serverMsg = (err as any)?.response?.data?.message;
      if (status === 409) {
        // Don't close — parent has refetched, the operator can re-confirm and
        // resubmit with the bumped lockVersion. Surface a specific notice.
        setSubmitError(
          serverMsg ||
            'Someone else just updated this workplace — review the latest values and resubmit.',
        );
      } else {
        // 400 (rootDir backend check) is handled by the parent: it pops the
        // ForceUnsafeRootModal, and on confirm resubmits this form's body
        // with forceUnsafeRoot: true via onSubmit. By the time the error
        // re-reaches us here, either the operator cancelled (and we surface
        // the original message) or a different 400 fired (collision /
        // format), which also belongs inline.
        setSubmitError(serverMsg || (err instanceof Error ? err.message : 'Submission failed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const setField = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // Special-case Name → Prefix → Root directory cascade:
  //   - Name typing auto-derives Prefix (unless user has manually edited Prefix)
  //   - Prefix changing auto-derives Root directory (unless user has manually
  //     edited Root) using the smart base path from `suggestedRootBase`.
  // Add-mode only; in edit mode the *ManuallyEdited flags start true so these
  // branches never fire.
  const handleNameChange = (value: string) => {
    setForm((prev) => {
      const nextPrefix = prefixManuallyEdited ? prev.prefix : deriveDefaultPrefix(value);
      const nextRootDir = rootDirManuallyEdited ? prev.rootDir : deriveRootDir(nextPrefix);
      return { ...prev, name: value, prefix: nextPrefix, rootDir: nextRootDir };
    });
  };

  const handlePrefixChange = (value: string) => {
    if (!prefixManuallyEdited) setPrefixManuallyEdited(true);
    setForm((prev) => {
      const nextRootDir = rootDirManuallyEdited ? prev.rootDir : deriveRootDir(value);
      return { ...prev, prefix: value, rootDir: nextRootDir };
    });
  };

  const handleRootDirChange = (value: string) => {
    if (!rootDirManuallyEdited) setRootDirManuallyEdited(true);
    setField('rootDir', value);
  };

  const toggleFlag = (flag: string, on: boolean) =>
    setForm((prev) => ({
      ...prev,
      serviceFlags: { ...prev.serviceFlags, [flag]: on },
    }));

  const title =
    mode === 'edit'
      ? `Edit Workplace Services Config${initialWorkplace ? ` — ${initialWorkplace.name}` : ''}`
      : 'Add Workplace Services Config';
  const submitLabel = submitting ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Add workplace';

  return (
    <BaseModal
      show={show}
      handleShow={onClose}
      size="lg"
      scrollable
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={!canSubmit}>
            {submitLabel}
          </Button>
        </>
      }
    >
      <Form>
        <Row className="g-3">
          <Col md={6}>
            <Form.Group controlId="wfm-name">
              <LabelWithHelp text="Name" helpId="wfm-name-help" helpText={FIELD_HELP.name} />
              <Form.Control
                value={form.name}
                onChange={(e) => handleNameChange(e.target.value)}
                isInvalid={!!errors.name}
                placeholder="QA"
              />
              <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="wfm-id">
              <LabelWithHelp
                text="Workplace ID (licensing-DB)"
                helpId="wfm-id-help"
                helpText={FIELD_HELP.id}
              />
              <Form.Control
                value={form.id}
                onChange={(e) => setField('id', e.target.value)}
                isInvalid={!!errors.id}
                placeholder="50"
                inputMode="numeric"
                disabled={mode === 'edit'}
                className={mode === 'edit' ? styles.disabledOnDark : undefined}
                style={
                  mode === 'edit'
                    ? {
                        backgroundColor: 'var(--color-primary-shadow)',
                        color: '#fff',
                        borderColor: 'var(--color-divider)',
                        WebkitTextFillColor: '#fff',
                      }
                    : undefined
                }
              />
              <Form.Control.Feedback type="invalid">{errors.id}</Form.Control.Feedback>
              {mode === 'edit' && (
                <Form.Text className="text-theme-muted">
                  ID is fixed once the workplace exists. Remove + add to change it.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="wfm-prefix">
              <LabelWithHelp text="Prefix" helpId="wfm-prefix-help" helpText={FIELD_HELP.prefix} />
              <Form.Control
                value={form.prefix}
                onChange={(e) => handlePrefixChange(e.target.value)}
                isInvalid={!!errors.prefix}
                placeholder="QA"
              />
              <Form.Control.Feedback type="invalid">{errors.prefix}</Form.Control.Feedback>
              <Form.Text className="text-theme-muted">
                {mode === 'add' && !prefixManuallyEdited
                  ? 'Auto-derived from Name — edit to override.'
                  : 'Used in PM2 process names.'}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="wfm-port">
              <LabelWithHelp text="Port" helpId="wfm-port-help" helpText={FIELD_HELP.port} />
              <Form.Control
                value={form.port}
                onChange={(e) => setField('port', e.target.value)}
                isInvalid={!!errors.port}
                placeholder={String(suggestedPort)}
                inputMode="numeric"
              />
              <Form.Control.Feedback type="invalid">{errors.port}</Form.Control.Feedback>
              {mode === 'add' && form.port === String(suggestedPort) && (
                <Form.Text className="text-theme-muted">
                  Suggested as next free port; edit to override.
                </Form.Text>
              )}
            </Form.Group>
          </Col>
          <Col md={12}>
            <Form.Group controlId="wfm-root">
              <LabelWithHelp
                text="Root directory"
                helpId="wfm-root-help"
                helpText={FIELD_HELP.rootDir}
              />
              <Form.Control
                value={form.rootDir}
                onChange={(e) => handleRootDirChange(e.target.value)}
                isInvalid={!!errors.rootDir}
                placeholder={
                  rootDirBase
                    ? `${rootDirBase}${form.prefix || 'QA'}`
                    : '/home/overwatchadmin/BackendServer_Fusion_QA'
                }
              />
              <Form.Control.Feedback type="invalid">{errors.rootDir}</Form.Control.Feedback>
              <Form.Text className="text-theme-muted">
                {mode === 'add' && !rootDirManuallyEdited && rootDirBase
                  ? 'Auto-derived from Prefix using existing workplace path — edit to override.'
                  : 'Absolute path on the supervisor host. Existence is verified server-side.'}
              </Form.Text>
            </Form.Group>
          </Col>
          <Col md={6}>
            <Form.Group controlId="wfm-mode">
              <LabelWithHelp text="Mode" helpId="wfm-mode-help" helpText={FIELD_HELP.mode} />
              <BaseSelect
                inputId="wfm-mode"
                isSearchable={false}
                selectProps={{
                  'aria-label': 'Workplace supervisor mode',
                  options: MODE_OPTIONS,
                  value: findSelectOption(MODE_OPTIONS, form.mode),
                  onChange: (option: { value: WorkplaceMode } | null) => {
                    if (option) setField('mode', option.value);
                  },
                }}
              />
            </Form.Group>
          </Col>
          {mode === 'add' && (
            <Col md={12}>
              <Form.Group controlId="wfm-service-flags">
                <LabelWithHelp
                  text="Service flags"
                  helpId="wfm-flags-help"
                  helpText={FIELD_HELP.serviceFlags}
                />
                <div className="text-theme-muted small mb-2">
                  Per-workplace overrides for each known service. Defaults to ON for everything;
                  uncheck to keep that service off for this workplace.
                </div>
                {knownEnvFlags.length === 0 ? (
                  <div className="text-theme-muted small">
                    No service flags discovered from the current workplaces listing.
                  </div>
                ) : (
                  <Row className="g-2">
                    {knownEnvFlags.map((flag) => (
                      <Col key={flag} xs={12} sm={6} md={4}>
                        <Form.Check
                          type="checkbox"
                          id={`wfm-flag-${flag}`}
                          label={flag}
                          checked={form.serviceFlags[flag] ?? true}
                          onChange={(e) => toggleFlag(flag, e.target.checked)}
                        />
                      </Col>
                    ))}
                  </Row>
                )}
              </Form.Group>
            </Col>
          )}
          {mode === 'edit' && (
            <Col md={12}>
              <div className="text-theme-muted small">
                Per-service overrides aren’t edited here — they’re controlled by the inline toggle
                column on the workplace’s services table.
              </div>
            </Col>
          )}
          <Col md={12}>
            <Form.Group controlId="wfm-reason">
              <LabelWithHelp text="Reason" helpId="wfm-reason-help" helpText={FIELD_HELP.reason} />
              <Form.Control
                as="textarea"
                rows={2}
                value={form.reason}
                onChange={(e) => setField('reason', e.target.value)}
                isInvalid={!!errors.reason}
                placeholder={
                  mode === 'edit'
                    ? 'Adjusting port for the move to box 2'
                    : 'Standing up QA deployment'
                }
              />
              <Form.Control.Feedback type="invalid">{errors.reason}</Form.Control.Feedback>
              <Form.Text className="text-theme-muted">
                Logged to command history for audit.
              </Form.Text>
            </Form.Group>
          </Col>
        </Row>
        {submitError && (
          <div className="text-danger mt-3">
            {mode === 'edit' ? 'Save failed' : 'Add failed'} — {submitError}
          </div>
        )}
      </Form>
    </BaseModal>
  );
}

interface LabelWithHelpProps {
  text: string;
  helpId: string;
  helpText: string;
}

/**
 * Form label with an adjacent info-icon tooltip explaining the field.
 *
 * Uses OverlayTrigger directly (not BaseTooltip) so we can portal the tooltip
 * to `document.body` via the `container` prop. Without that, react-bootstrap
 * renders the tooltip inside the modal's stacking context, where it gets
 * painted *behind* the modal backdrop.
 *
 * The global Bootstrap binding puts tooltips on `--z-popover`, above every
 * BaseModal level. Bootstrap's default tier was too low and hid these tooltips
 * behind the Edit modal.
 */
function LabelWithHelp({ text, helpId, helpText }: LabelWithHelpProps) {
  const portalContainer = typeof document !== 'undefined' ? document.body : undefined;
  return (
    <Form.Label className="text-theme d-flex align-items-center gap-2">
      <span>{text}</span>
      <OverlayTrigger
        trigger={['hover', 'focus']}
        placement="top"
        container={portalContainer}
        overlay={
          <Tooltip id={helpId} className="responder-tooltip">
            {helpText}
          </Tooltip>
        }
      >
        <span
          role="button"
          tabIndex={0}
          aria-label={`Help: ${text}`}
          className="text-theme-muted d-inline-flex"
        >
          <FaInfoCircle />
        </span>
      </OverlayTrigger>
    </Form.Label>
  );
}
